import { ReadonlyVec3, vec3 } from 'gl-matrix';

/**
 * Pure geometry in the patient frame, in mm: points and the planes they are
 * tested against.
 *
 * Patient space already encodes voxel spacing and orientation, so a Euclidean
 * distance here is physically exact. Do NOT re-multiply by voxel spacing.
 */

/** A point in the patient coordinate frame, in mm. */
export type PointMm = readonly [number, number, number];

/** A gl-matrix vector as a plain mm tuple: serializable, and double precision. */
export function toPointMm(point: ReadonlyVec3): PointMm {
    return [point[0], point[1], point[2]];
}

/** Euclidean distance in mm. Zero is a valid result; the caller decides. */
export function euclideanDistanceMm(a: PointMm, b: PointMm): number {
    return vec3.distance(a, b);
}

/** Midpoint in mm, in double precision. */
export function midpointMm(a: PointMm, b: PointMm): PointMm {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

/**
 * Interior angle at `vertex`, degrees [0, 180]. `atan2(|u × v|, u · v)` stays
 * precise for near-collinear arms where `acos` does not.
 *
 * @returns NaN for a zero-length arm, which the caller must refuse.
 */
export function angleDegAtVertex(
    armA: PointMm,
    vertex: PointMm,
    armB: PointMm
): number {
    const u = vec3.subtract(vec3.create(), armA, vertex);
    const v = vec3.subtract(vec3.create(), armB, vertex);
    if (vec3.length(u) === 0 || vec3.length(v) === 0) {
        return NaN;
    }
    const crossMagnitude = vec3.length(vec3.cross(vec3.create(), u, v));
    return (Math.atan2(crossMagnitude, vec3.dot(u, v)) * 180) / Math.PI;
}

/**
 * A plane through `point` with unit `normal`, patient space (mm). The
 * equivalent `offsetMm` form used by the helpers below is
 * `dot(point, normal)`.
 */
export interface PlaneMm {
    readonly point: ReadonlyVec3;
    readonly normal: ReadonlyVec3;
}

/**
 * The plane's signed offset along its own normal — the `offsetMm` form the
 * helpers below take. Two planes with the same normal and the same offset are
 * the same plane, however far apart their `point`s lie within it.
 */
export function planeOffsetMm(plane: PlaneMm): number {
    return (
        plane.point[0] * plane.normal[0] +
        plane.point[1] * plane.normal[1] +
        plane.point[2] * plane.normal[2]
    );
}

/** A plane is a unit `normal` plus its `offsetMm` along that normal. */
export function planeDistanceMm(
    point: PointMm,
    normal: ReadonlyVec3,
    offsetMm: number
): number {
    return (
        point[0] * normal[0] +
        point[1] * normal[1] +
        point[2] * normal[2] -
        offsetMm
    );
}

/** Whether EVERY point lies within `toleranceMm` of the plane. */
export function arePointsOnPlane(
    points: readonly PointMm[],
    normal: ReadonlyVec3,
    offsetMm: number,
    toleranceMm: number
): boolean {
    return points.every(
        (point) =>
            Math.abs(planeDistanceMm(point, normal, offsetMm)) <= toleranceMm
    );
}

/** `point` moved along `normal` onto the plane, keeping its in-plane position. */
export function projectPointOntoPlane(
    point: PointMm,
    normal: ReadonlyVec3,
    offsetMm: number
): PointMm {
    const distanceMm = planeDistanceMm(point, normal, offsetMm);
    const projected = vec3.scaleAndAdd(
        vec3.create(),
        point,
        normal,
        -distanceMm
    );
    return [projected[0], projected[1], projected[2]];
}

/** A patient axis by index: 0 is LR (X), 1 is AP (Y), 2 is SI (Z). */
type PatientAxisIndex = 0 | 1 | 2;

/** A point in a plane's own frame, along its `u` and `v` axes, mm. */
export type Vec2Mm = readonly [number, number];

/** A plane through `originMm` with unit `normal` and an orthonormal in-plane basis `u`, `v`. */
export interface PlaneFrame {
    readonly originMm: PointMm;
    readonly normal: PointMm;
    readonly u: PointMm;
    readonly v: PointMm;
}

const NORMAL_LENGTH_EPSILON = 1e-9;
const PARALLEL_TOLERANCE_DEG = 0.1;
const COINCIDENT_TOLERANCE_MM = 0.05;

function dot3(a: PointMm, b: PointMm): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross3(a: PointMm, b: PointMm): PointMm {
    return [
        a[1] * b[2] - a[2] * b[1],
        a[2] * b[0] - a[0] * b[2],
        a[0] * b[1] - a[1] * b[0],
    ];
}

function sub3(a: PointMm, b: PointMm): PointMm {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function length3(a: PointMm): number {
    return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
}

/** `a` at unit length, or undefined when it has no direction. */
function normalise3(a: PointMm): PointMm | undefined {
    const len = length3(a);
    if (!Number.isFinite(len) || len < NORMAL_LENGTH_EPSILON) {
        return undefined;
    }
    return [a[0] / len, a[1] / len, a[2] / len];
}

/**
 * The frame of `plane`, `u` seeded from the axis least aligned with its
 * normal. Undefined for a plane with a non-finite point or no normal.
 */
export function planeFrameOf(plane: PlaneMm): PlaneFrame | undefined {
    const originMm = toPointMm(plane.point);
    const normal = normalise3(toPointMm(plane.normal));
    if (!originMm.every((c) => Number.isFinite(c)) || !normal) {
        return undefined;
    }
    let axis = 0;
    for (let i = 1; i < 3; i++) {
        axis = Math.abs(normal[i]) < Math.abs(normal[axis]) ? i : axis;
    }
    const e: PointMm = [+(axis === 0), +(axis === 1), +(axis === 2)];
    const along = dot3(e, normal);
    const u = normalise3([
        e[0] - along * normal[0],
        e[1] - along * normal[1],
        e[2] - along * normal[2],
    ]);
    return u && { originMm, normal, u, v: cross3(normal, u) };
}

/** `pMm` in the plane's own frame (its foot on the plane). */
export function projectToPlane(frame: PlaneFrame, pMm: PointMm): Vec2Mm {
    const d = sub3(pMm, frame.originMm);
    return [dot3(d, frame.u), dot3(d, frame.v)];
}

/** The patient point at `uv` in the plane's own frame. */
export function liftFromPlane(frame: PlaneFrame, uv: Vec2Mm): PointMm {
    const { originMm, u, v } = frame;
    return [
        originMm[0] + uv[0] * u[0] + uv[1] * v[0],
        originMm[1] + uv[0] * u[1] + uv[1] * v[1],
        originMm[2] + uv[0] * u[2] + uv[1] * v[2],
    ];
}

/**
 * Signed distance of `pMm` from the frame's plane, measured from its origin;
 * {@link planeDistanceMm} takes the plane as a normal and an offset.
 */
export function planeSignedDistanceMm(frame: PlaneFrame, pMm: PointMm): number {
    return dot3(sub3(pMm, frame.originMm), frame.normal);
}

/** Whether two frames lie on the same plane, within the given angle and separation. */
export function planesAreCoincident(
    a: PlaneFrame,
    b: PlaneFrame,
    toleranceDeg: number = PARALLEL_TOLERANCE_DEG,
    separationToleranceMm: number = COINCIDENT_TOLERANCE_MM
): boolean {
    const sinAngle = length3(cross3(a.normal, b.normal));
    return (
        sinAngle <= Math.sin((toleranceDeg * Math.PI) / 180) &&
        Math.abs(planeSignedDistanceMm(a, b.originMm)) <= separationToleranceMm
    );
}

/** Where along segment ab, from 0 at a to 1 at b, the point nearest to p lies. */
function nearestOnSegment(p: Vec2Mm, a: Vec2Mm, b: Vec2Mm): number {
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const lenSq = abx * abx + aby * aby;
    return lenSq > 0
        ? Math.min(
              1,
              Math.max(0, ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / lenSq)
          )
        : 0;
}

/** The point of segment ab nearest to p. */
export function closestPointOnSegment(p: Vec2Mm, a: Vec2Mm, b: Vec2Mm): Vec2Mm {
    const t = nearestOnSegment(p, a, b);
    return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
}

/** Distance from p to segment ab. */
export function pointSegmentDistance(p: Vec2Mm, a: Vec2Mm, b: Vec2Mm): number {
    const t = nearestOnSegment(p, a, b);
    const dx = p[0] - a[0] - t * (b[0] - a[0]);
    const dy = p[1] - a[1] - t * (b[1] - a[1]);
    return Math.sqrt(dx * dx + dy * dy);
}

/** The patient axis a direction runs closest to, and which way along it. */
export interface DominantComponent {
    index: PatientAxisIndex;
    /** The sign of that component; a zero component counts as positive. */
    sign: 1 | -1;
}

/**
 * The component of `direction` with the largest magnitude, the earliest
 * axis winning a tie. A patient-aligned direction has one non-zero
 * component; an oblique one is nearest the axis this names.
 */
export function dominantComponent(direction: ReadonlyVec3): DominantComponent {
    let index: PatientAxisIndex = 0;
    for (const candidate of [1, 2] as const) {
        if (Math.abs(direction[candidate]) > Math.abs(direction[index])) {
            index = candidate;
        }
    }
    return { index, sign: direction[index] < 0 ? -1 : 1 };
}
